import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Send,
} from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_LINE = "______________________________";
const BLANK_LINE_SHORT = "______________";

const initialForm = {
  name: "",
  father_or_husband_name: "",
  address: "",
  dob: "",
  mobile: "",
  email: "",
  education: "",
  thana: "",
  district: "",
  place: "",
  application_date: "",
};

// Field configs for the form UI
const FIELDS = [
  {
    name: "name",
    label: "Full Name",
    labelHi: "पूरा नाम",
    placeholder: "e.g. Rajesh Kumar Sharma",
    required: true,
  },
  {
    name: "father_or_husband_name",
    label: "Father's / Husband's Name",
    labelHi: "पिता / पति का नाम",
    placeholder: "e.g. Suresh Kumar Sharma",
    required: true,
  },
  {
    name: "dob",
    label: "Date of Birth",
    labelHi: "जन्मतिथि",
    placeholder: "DD/MM/YYYY",
    required: true,
  },
  {
    name: "mobile",
    label: "Mobile Number",
    labelHi: "मोबाइल नंबर",
    placeholder: "10-digit number",
    required: true,
    type: "tel",
  },
  {
    name: "email",
    label: "Email (to receive PDF)",
    labelHi: "ईमेल (PDF प्राप्त करने हेतु)",
    placeholder: "you@example.com",
    type: "email",
  },
  {
    name: "education",
    label: "Educational Qualification",
    labelHi: "शैक्षणिक योग्यता",
    placeholder: "e.g. 10th Pass / Graduate",
    required: true,
  },
  {
    name: "address",
    label: "Residential Address",
    labelHi: "पता",
    placeholder: "House no., street, city, district, state, PIN",
    required: true,
    textarea: true,
  },
  {
    name: "thana",
    label: "Police Station (Thana)",
    labelHi: "थाना",
    placeholder: "e.g. Mahakal Police Station",
    required: true,
  },
  {
    name: "district",
    label: "District",
    labelHi: "जिला",
    placeholder: "e.g. Ujjain",
    required: true,
  },
  {
    name: "place",
    label: "Place (for signature)",
    labelHi: "स्थान",
    placeholder: "e.g. Ujjain",
  },
  {
    name: "application_date",
    label: "Application Date",
    labelHi: "दिनांक",
    placeholder: "DD/MM/2026",
  },
];

// --- The visual PDF template (Hindi) ----------------------------------------
function HindiLetterTemplate({ data, blank }) {
  const v = blank
    ? {
        name: BLANK_LINE,
        father_or_husband_name: BLANK_LINE,
        address: BLANK_LINE + BLANK_LINE,
        dob: BLANK_LINE_SHORT,
        mobile: BLANK_LINE_SHORT,
        education: BLANK_LINE_SHORT,
        thana: BLANK_LINE_SHORT,
        district: BLANK_LINE_SHORT,
        place: BLANK_LINE_SHORT,
        application_date: "___ / ___ / 2026",
      }
    : {
        name: data.name || BLANK_LINE,
        father_or_husband_name: data.father_or_husband_name || BLANK_LINE,
        address: data.address || BLANK_LINE + BLANK_LINE,
        dob: data.dob || BLANK_LINE_SHORT,
        mobile: data.mobile || BLANK_LINE_SHORT,
        education: data.education || BLANK_LINE_SHORT,
        thana: data.thana || BLANK_LINE_SHORT,
        district: data.district || BLANK_LINE_SHORT,
        place: data.place || BLANK_LINE_SHORT,
        application_date: data.application_date || "___ / ___ / 2026",
      };

  return (
    <div
      style={{
        width: "794px", // A4 width at ~96 DPI
        minHeight: "1123px",
        padding: "70px 70px",
        background: "#ffffff",
        color: "#111111",
        fontFamily:
          "'Noto Serif Devanagari', 'Noto Sans Devanagari', serif",
        fontSize: "16px",
        lineHeight: "1.85",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: "26px" }}>
        <div>सेवा में,</div>
        <div>थाना प्रभारी महोदय,</div>
        <div>
          थाना <u>&nbsp;{v.thana}&nbsp;</u>,
        </div>
        <div>
          जिला <u>&nbsp;{v.district}&nbsp;</u>
        </div>
      </div>

      <div style={{ marginBottom: "20px", fontWeight: 600 }}>
        विषय: Ujjain Simhastha 2028 में स्वयंसेवक (Volunteer) के रूप में पंजीयन
        हेतु आवेदन।
      </div>

      <div style={{ marginBottom: "18px" }}>महोदय,</div>

      <div style={{ textAlign: "justify", marginBottom: "18px" }}>
        सविनय निवेदन है कि मैं <u>&nbsp;{v.name}&nbsp;</u> (अपना नाम),
        पिता/पति <u>&nbsp;{v.father_or_husband_name}&nbsp;</u>, निवासी{" "}
        <u>&nbsp;{v.address}&nbsp;</u>, Ujjain Simhastha 2028 में स्वयंसेवक
        (Volunteer) के रूप में अपनी सेवाएं प्रदान करना चाहता / चाहती हूँ।
      </div>

      <div style={{ textAlign: "justify", marginBottom: "22px" }}>
        अतः आपसे विनम्र अनुरोध है कि मेरा पंजीयन स्वयंसेवक के रूप में स्वीकार
        करने की कृपा करें। मैंने इस आवेदन पत्र के साथ अपनी कक्षा 10वीं की
        अंकसूची (Marksheet) की छायाप्रति संलग्न की है।
      </div>

      <div style={{ marginBottom: "10px", fontWeight: 600 }}>
        मेरी जानकारी निम्नानुसार है:
      </div>

      <div style={{ marginBottom: "22px", lineHeight: "2.1" }}>
        <div>
          नाम: <u>&nbsp;{v.name}&nbsp;</u>
        </div>
        <div>
          पिता/पति का नाम: <u>&nbsp;{v.father_or_husband_name}&nbsp;</u>
        </div>
        <div>
          जन्मतिथि: <u>&nbsp;{v.dob}&nbsp;</u>
        </div>
        <div>
          मोबाइल नंबर: <u>&nbsp;{v.mobile}&nbsp;</u>
        </div>
        <div>
          पता: <u>&nbsp;{v.address}&nbsp;</u>
        </div>
        <div>
          शैक्षणिक योग्यता: <u>&nbsp;{v.education}&nbsp;</u>
        </div>
      </div>

      <div style={{ marginBottom: "12px", fontWeight: 600 }}>संलग्नक:</div>
      <ol style={{ paddingLeft: "24px", marginBottom: "26px" }}>
        <li>कक्षा 10वीं की अंकसूची की छायाप्रति।</li>
        <li>अन्य आवश्यक दस्तावेज (यदि कोई हो)।</li>
      </ol>

      <div style={{ marginBottom: "8px" }}>
        दिनांक: <u>&nbsp;{v.application_date}&nbsp;</u>
      </div>
      <div style={{ marginBottom: "32px" }}>
        स्थान: <u>&nbsp;{v.place}&nbsp;</u>
      </div>

      <div>भवदीय,</div>
      <div style={{ marginTop: "60px" }}>(हस्ताक्षर)</div>
      <div style={{ marginTop: "6px" }}>
        नाम: <u>&nbsp;{v.name}&nbsp;</u>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

export default function VolunteerApplicationForm() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatingBlank, setGeneratingBlank] = useState(false);
  const [success, setSuccess] = useState(null); // { emailSent, emailError, id }
  const filledRef = useRef(null);
  const blankRef = useRef(null);

  const update = (field, val) => {
    setForm((f) => ({ ...f, [field]: val }));
    if (success) setSuccess(null);
    if (error) setError("");
  };

  // SEO: dynamic title + description
  useEffect(() => {
    const prev = document.title;
    document.title =
      "Volunteer Application Letter (Hindi PDF) — Ujjain Simhastha 2028";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta)
      meta.setAttribute(
        "content",
        "Generate a free, ready-to-print Hindi (Devanagari) volunteer / police-verification application letter for Ujjain Simhastha 2028. Fill the bilingual form and download a personalised PDF instantly.",
      );
    return () => {
      document.title = prev;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  // Validate required fields
  const validate = () => {
    for (const f of FIELDS) {
      if (f.required && !(form[f.name] || "").trim()) {
        return `${f.label} is required / ${f.labelHi} आवश्यक है`;
      }
    }
    if (form.mobile && !/^\d{10,15}$/.test(form.mobile.trim())) {
      return "Enter a valid mobile number (10–15 digits)";
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Enter a valid email address";
    }
    return "";
  };

  // Build PDF blob from a hidden DOM node
  const buildPdfBlob = async (node) => {
    // Ensure web fonts are ready (Noto Devanagari)
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
    } catch (_) {
      // ignore — html2canvas will still attempt to render
    }
    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    // Maintain aspect ratio, fit width
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // If content is taller than one page, split into multiple pages
    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
    } else {
      // Multi-page handling by slicing the canvas
      let position = 0;
      const sliceHeightPx = (pageHeight * canvas.width) / pageWidth;
      let remaining = canvas.height;
      while (remaining > 0) {
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = Math.min(sliceHeightPx, remaining);
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          position,
          canvas.width,
          sliceCanvas.height,
          0,
          0,
          canvas.width,
          sliceCanvas.height,
        );
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceImgHeight = (sliceCanvas.height * imgWidth) / canvas.width;
        if (position > 0) pdf.addPage();
        pdf.addImage(sliceData, "JPEG", 0, 0, imgWidth, sliceImgHeight);
        position += sliceCanvas.height;
        remaining -= sliceCanvas.height;
      }
    }

    return {
      blob: pdf.output("blob"),
      base64: pdf.output("datauristring"), // includes "data:application/pdf;base64,..."
    };
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleGenerateFilled = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setGenerating(true);
    setSuccess(null);
    try {
      const { blob, base64 } = await buildPdfBlob(filledRef.current);
      triggerDownload(
        blob,
        `Volunteer-Application-${form.name.replace(/\s+/g, "_")}.pdf`,
      );

      // Save to backend (DB + email)
      try {
        const res = await axios.post(`${API}/volunteer-application`, {
          ...form,
          pdf_base64: base64,
        });
        setSuccess({
          id: res.data?.id || "",
          emailSent: !!res.data?.email_sent,
          emailError: res.data?.email_error || "",
        });
      } catch (saveErr) {
        // PDF was downloaded — show partial success
        setSuccess({
          id: "",
          emailSent: false,
          emailError:
            saveErr?.response?.data?.detail ||
            saveErr?.message ||
            "Could not save submission",
        });
      }
    } catch (err) {
      setError(
        err?.message ||
          "Failed to generate PDF. Please try again or use a different browser.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadBlank = async () => {
    setError("");
    setGeneratingBlank(true);
    try {
      const { blob } = await buildPdfBlob(blankRef.current);
      triggerDownload(blob, "Volunteer-Application-Blank-Hindi.pdf");
    } catch (err) {
      setError(err?.message || "Failed to generate blank PDF");
    } finally {
      setGeneratingBlank(false);
    }
  };

  return (
    <div
      className="min-h-screen pt-28 pb-20"
      style={{ background: "#1c1917" }}
      data-testid="volunteer-application-page"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/tools"
          className="inline-flex items-center gap-2 text-white/50 hover:text-saffron text-sm font-body mb-6 transition-colors"
          data-testid="back-to-tools-link"
        >
          <ArrowLeft size={16} /> Back to Tools
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-saffron/10 border border-saffron/20 mb-4">
            <FileText size={14} className="text-saffron" />
            <span className="text-saffron text-xs font-body uppercase tracking-widest">
              Volunteer Application
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight mb-2">
            Volunteer Application Letter
          </h1>
          <p
            className="text-white/55 text-sm sm:text-base mb-2"
            style={{
              fontFamily:
                "'Noto Sans Devanagari', 'Noto Serif Devanagari', sans-serif",
            }}
          >
            उज्जैन सिंहस्थ 2028 — स्वयंसेवक आवेदन पत्र
          </p>
          <p className="text-white/55 text-sm font-body max-w-3xl leading-relaxed">
            Fill the form below to generate a ready-to-print Hindi application
            letter addressed to your local Police Station for volunteer
            verification. The filled PDF is downloaded instantly and a copy is
            emailed to you.
          </p>
        </motion.div>

        {/* Blank form download — prominent secondary action */}
        <div
          className="mb-8 p-4 rounded-2xl border border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          style={{ background: "rgba(255,255,255,0.03)" }}
        >
          <div>
            <p className="text-white text-sm font-body font-semibold">
              Just want a blank printable form?
            </p>
            <p className="text-white/50 text-xs font-body">
              Download a blank Hindi template you can fill by hand.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadBlank}
            disabled={generatingBlank}
            data-testid="download-blank-pdf-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-body font-medium border border-white/15 transition-all disabled:opacity-50"
          >
            {generatingBlank ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Download Blank Form
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleGenerateFilled}
          className="rounded-2xl border border-white/10 p-6 sm:p-8"
          style={{ background: "rgba(255,255,255,0.03)" }}
          data-testid="volunteer-application-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div
                key={f.name}
                className={f.textarea ? "md:col-span-2" : ""}
              >
                <label className="block mb-1.5">
                  <span className="text-white/60 text-xs uppercase tracking-widest font-body">
                    {f.label}
                    {f.required && (
                      <span className="text-saffron ml-1">*</span>
                    )}
                  </span>
                  <span
                    className="block text-white/35 text-xs"
                    style={{
                      fontFamily:
                        "'Noto Sans Devanagari', 'Noto Serif Devanagari', sans-serif",
                    }}
                  >
                    {f.labelHi}
                  </span>
                </label>
                {f.textarea ? (
                  <textarea
                    data-testid={`field-${f.name}`}
                    value={form[f.name]}
                    onChange={(e) => update(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white placeholder:text-white/25 text-sm font-body outline-none transition-all resize-none"
                  />
                ) : (
                  <input
                    data-testid={`field-${f.name}`}
                    type={f.type || "text"}
                    value={form[f.name]}
                    onChange={(e) => update(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white placeholder:text-white/25 text-sm font-body outline-none transition-all"
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div
              className="mt-5 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-body flex items-start gap-2"
              data-testid="form-error"
            >
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div
              className="mt-5 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm font-body"
              data-testid="form-success"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 flex-shrink-0 text-emerald-400"
                />
                <div className="space-y-1">
                  <div className="font-semibold text-emerald-300">
                    PDF generated and downloaded successfully!
                  </div>
                  {success.emailSent ? (
                    <div>
                      A copy has been emailed to{" "}
                      <span className="text-emerald-100 font-medium">
                        {form.email}
                      </span>
                      .
                    </div>
                  ) : success.emailError ? (
                    <div className="text-amber-200 text-xs">
                      Note: We couldn't email a copy ({success.emailError}). Your
                      downloaded PDF is still valid.
                    </div>
                  ) : (
                    <div className="text-white/60 text-xs">
                      Add your email above to receive a copy by mail.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={generating}
              data-testid="generate-pdf-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-saffron text-stone-900 hover:bg-saffron-light text-sm font-body font-semibold transition-all hover:shadow-[0_0_20px_rgba(255,153,51,0.4)] active:scale-95 disabled:opacity-60"
            >
              {generating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              Generate &amp; Download PDF
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(initialForm);
                setError("");
                setSuccess(null);
              }}
              data-testid="reset-form-btn"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-transparent text-white/70 hover:text-white hover:bg-white/5 text-sm font-body font-medium border border-white/15 transition-all"
            >
              Reset
            </button>
          </div>

          <p className="mt-5 text-[11px] text-white/40 font-body leading-relaxed">
            Disclaimer: This is a community-generated template letter. It is
            <strong className="text-white/60"> not an official government form</strong>.
            Please review the letter before signing and submitting it to your
            local police station.
          </p>
        </form>
      </div>

      {/* Hidden render area for html2canvas → jsPDF.
          Positioned off-screen but still in the DOM so fonts/layout render. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          pointerEvents: "none",
        }}
      >
        <div ref={filledRef}>
          <HindiLetterTemplate data={form} blank={false} />
        </div>
        <div ref={blankRef}>
          <HindiLetterTemplate data={initialForm} blank={true} />
        </div>
      </div>
    </div>
  );
}
